/**
 * Le terrain, adossé à Supabase.
 *
 * Une particularité domine ce fichier : l'ouvrier n'a **aucun** accès à
 * `projects`. Les missions ne se lisent donc pas par un `select` mais par la
 * fonction `my_missions()`, qui projette en base ce qui a le droit de sortir.
 * C'est ce qui remplace un filtre de colonnes que la RLS ne sait pas poser.
 */
import type {
  Assignment,
  CreateFieldReportCommand,
  DayPart,
  ExecutionContext,
  FieldReport,
  FieldRepository,
  Mission,
  SaveAssignmentCommand,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const ASSIGNMENT_COLUMNS = `
  id, project_id, employee_id, starts_on, ends_on, role_on_site, notes,
  works_saturday, works_sunday, day_part,
  project:projects(name),
  employee:employees(first_name, last_name)
`;

const REPORT_COLUMNS = `
  id, project_id, employee_id, kind, severity, message, status,
  created_at, resolved_at,
  phase_id, declared_progress_percent,
  project:projects(name),
  employee:employees(first_name, last_name),
  phase:project_phases(name)
`;

const one = (v: unknown): Row | null => (Array.isArray(v) ? (v[0] ?? null) : ((v as Row) ?? null));

const nameOf = (employee: Row | null): string | null => {
  if (!employee) return null;
  const full = [employee.first_name, employee.last_name].filter(Boolean).join(" ").trim();
  return full || null;
};

/** Une valeur inconnue — ou absente, d'une base pas encore migrée — vaut la journée. */
const toDayPart = (v: unknown): DayPart =>
  v === "matin" || v === "apres_midi" ? v : "journee";

function toMission(row: Row): Mission {
  // Les deux coordonnées vont ensemble ou pas du tout : une latitude sans
  // longitude ne mène nulle part, et produirait un lien d'itinéraire cassé.
  const lat = row.site_gps_lat == null ? null : Number(row.site_gps_lat);
  const lng = row.site_gps_lng == null ? null : Number(row.site_gps_lng);

  return {
    projectId: row.project_id,
    projectName: row.project_name ?? "",
    projectStatus: row.project_status ?? "",
    siteAddress: row.site_address ?? null,
    sitePostalCode: row.site_postal_code ?? null,
    siteCity: row.site_city ?? null,
    siteGps: lat != null && lng != null ? { lat, lng } : null,
    roleOnSite: row.role_on_site ?? null,
    startsOn: String(row.starts_on).slice(0, 10),
    endsOn: row.ends_on ? String(row.ends_on).slice(0, 10) : null,
    notes: row.notes ?? null,
    progressPercent: row.progress_percent == null ? null : Number(row.progress_percent),
    worksSaturday: row.works_saturday === true,
    worksSunday: row.works_sunday === true,
    dayPart: toDayPart(row.day_part),
  };
}

function toAssignment(row: Row): Assignment {
  const project = one(row.project);
  const employee = one(row.employee);
  return {
    id: row.id,
    projectId: row.project_id,
    projectName: project?.name ?? null,
    employeeId: row.employee_id,
    employeeName: nameOf(employee),
    startsOn: String(row.starts_on).slice(0, 10),
    endsOn: row.ends_on ? String(row.ends_on).slice(0, 10) : null,
    roleOnSite: row.role_on_site ?? null,
    notes: row.notes ?? null,
    worksSaturday: row.works_saturday === true,
    worksSunday: row.works_sunday === true,
    dayPart: toDayPart(row.day_part),
  };
}

function toReport(row: Row): FieldReport {
  const project = one(row.project);
  const employee = one(row.employee);
  return {
    id: row.id,
    projectId: row.project_id,
    projectName: project?.name ?? null,
    employeeId: row.employee_id ?? null,
    employeeName: nameOf(employee),
    kind: row.kind,
    severity: row.severity,
    message: row.message ?? "",
    status: row.status,
    createdAt: row.created_at,
    resolvedAt: row.resolved_at ?? null,
    phaseId: row.phase_id ?? null,
    phaseName: one(row.phase)?.name ?? null,
    declaredProgressPercent:
      row.declared_progress_percent == null ? null : Number(row.declared_progress_percent),
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseFieldRepository(
  clientFor: DbClientFactory,
): FieldRepository {
  return {
    async myMissions(ctx: ExecutionContext, on: string) {
      // Pas un `select` sur `projects` : l'appelant n'y a aucun droit, et
      // c'est voulu. La fonction ne rend que ce qui sert sur le terrain.
      const { data, error } = await clientFor(ctx).rpc("my_missions", { p_on: on });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toMission);
    },

    async myUpcomingMissions(ctx: ExecutionContext, days: number) {
      // Fonction distincte de `my_missions` et non un paramètre de plus : la
      // première est déployée, appelée en production et éprouvée sur un vrai
      // téléphone. On n'élargit pas la signature de ce qui marche.
      const { data, error } = await clientFor(ctx).rpc("my_upcoming_missions", {
        p_days: days,
      });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toMission);
    },

    async listAssignments(ctx, filter) {
      let query = clientFor(ctx)
        .from("project_assignments")
        .select(ASSIGNMENT_COLUMNS)
        .order("starts_on", { ascending: false });

      if (filter.projectId) query = query.eq("project_id", filter.projectId);
      if (filter.employeeId) query = query.eq("employee_id", filter.employeeId);
      // Chevauchement, pas inclusion : une affectation commencée le mois
      // dernier et toujours en cours concerne bien la semaine demandée. Une
      // fin nulle vaut « sans terme », d'où le `or` plutôt qu'un simple `gte`.
      if (filter.to) query = query.lte("starts_on", filter.to);
      if (filter.from) query = query.or(`ends_on.is.null,ends_on.gte.${filter.from}`);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toAssignment);
    },

    async saveAssignment(ctx: ExecutionContext, cmd: SaveAssignmentCommand) {
      const patch = {
        project_id: cmd.projectId,
        employee_id: cmd.employeeId,
        starts_on: cmd.startsOn,
        ends_on: cmd.endsOn,
        role_on_site: cmd.roleOnSite,
        notes: cmd.notes,
        works_saturday: cmd.worksSaturday,
        works_sunday: cmd.worksSunday,
        day_part: cmd.dayPart,
      };

      const query = cmd.assignmentId
        ? clientFor(ctx)
            .from("project_assignments")
            .update(patch)
            .eq("id", cmd.assignmentId)
        : clientFor(ctx).from("project_assignments").insert(patch);

      const { data, error } = await query.select(ASSIGNMENT_COLUMNS).single();
      if (error) fail(error);
      return toAssignment(data as Row);
    },

    async removeAssignment(ctx: ExecutionContext, assignmentId: string) {
      const { error } = await clientFor(ctx)
        .from("project_assignments")
        .delete()
        .eq("id", assignmentId);
      if (error) fail(error);
    },

    async createReport(ctx: ExecutionContext, cmd: CreateFieldReportCommand) {
      // `employee_id` et `reported_by` sont posés ici plutôt que laissés à un
      // défaut : le premier vient de la fonction qui relie compte et salarié,
      // le second de la session. Sans le premier, l'alerte qui part au patron
      // ne nommerait personne — et il rappelle une personne, pas un chantier.
      const { data: employeeId } = await clientFor(ctx).rpc("my_employee_id");

      const { data, error } = await clientFor(ctx)
        .from("field_reports")
        .insert({
          project_id: cmd.projectId,
          employee_id: employeeId ?? null,
          reported_by: ctx.actorId,
          kind: cmd.kind,
          severity: cmd.severity,
          message: cmd.message,
        })
        .select(REPORT_COLUMNS)
        .single();

      if (error) fail(error);
      return toReport(data as Row);
    },

    async listReports(ctx, filter) {
      let query = clientFor(ctx)
        .from("field_reports")
        .select(REPORT_COLUMNS)
        .order("created_at", { ascending: false });

      if (!filter.includeResolved) query = query.neq("status", "resolved");
      if (filter.projectId) query = query.eq("project_id", filter.projectId);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toReport);
    },

    async setReportStatus(ctx, reportId, status) {
      const { data, error } = await clientFor(ctx)
        .from("field_reports")
        .update({
          status,
          resolved_at: status === "resolved" ? new Date().toISOString() : null,
          resolved_by: status === "resolved" ? ctx.actorId : null,
        })
        .eq("id", reportId)
        .select(REPORT_COLUMNS)
        .single();

      if (error) fail(error);
      return toReport(data as Row);
    },

    async myPhases(ctx: ExecutionContext, projectId: string) {
      // Par la fonction et non par la table : `project_phases` est fermée à
      // l'ouvrier, et c'est la projection en base qui tient lieu de
      // cloisonnement — la RLS filtre des lignes, jamais des colonnes.
      const { data, error } = await clientFor(ctx).rpc("my_project_phases", {
        p_project_id: projectId,
      });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map((p) => ({
        id: p.id as string,
        name: p.name as string,
        sortOrder: Number(p.sort_order ?? 0),
        progressPercent: Number(p.progress_percent ?? 0),
        status: (p.status as string) ?? "pending",
        lotName: (p.lot_name as string | null) ?? null,
      }));
    },

    async myCallContacts(ctx: ExecutionContext) {
      // Même raison que `myPhases` : `users` porte le rôle de chacun, donc la
      // carte des droits de l'entreprise, et un ouvrier ne la lit pas. La
      // fonction ne rend que trois colonnes.
      const { data, error } = await clientFor(ctx).rpc("my_call_contacts");
      if (error) fail(error);
      return ((data ?? []) as Row[]).map((c) => ({
        userId: c.user_id as string,
        fullName: (c.full_name as string) ?? "",
        role: (c.role as string) ?? "",
        phone: (c.phone as string) ?? "",
      }));
    },

    async declareProgress(ctx: ExecutionContext, cmd) {
      // La fonction résout le chantier depuis la phase et vérifie
      // l'affectation : l'appelant ne peut pas lire `project_phases`, donc il
      // ne connaît pas le `project_id` que `field_reports` exige.
      const { data, error } = await clientFor(ctx).rpc("declare_phase_progress", {
        p_phase_id: cmd.phaseId,
        p_percent: cmd.percent,
        p_message: cmd.message,
      });
      if (error) fail(error);

      // La fonction rend la ligne brute, sans les jointures du listing. Une
      // relecture donne au retour la même forme que partout ailleurs, plutôt
      // que deux formes de `FieldReport` selon le chemin emprunté.
      const { data: complet, error: eRelecture } = await clientFor(ctx)
        .from("field_reports")
        .select(REPORT_COLUMNS)
        .eq("id", (data as Row).id)
        .single();
      if (eRelecture) fail(eRelecture);
      return toReport(complet as Row);
    },

    async applyDeclaredProgress(ctx: ExecutionContext, reportId: string) {
      const { error } = await clientFor(ctx).rpc("apply_declared_progress", {
        p_report_id: reportId,
      });
      if (error) fail(error);
    },
  };
}
